/**
 * MyPage Hook
 * 마이페이지 데이터 관리 및 API 호출 로직
 */
import { useCallback, useEffect, useState, useRef } from 'react';
import { useAsync } from '../../../../core/hooks/useAsync';
import { getMyPageInfo, updateMyPageSettlement } from '../api/myPageApi';
import {
  MyPageInfoResponse,
  MyPageSettlementUpdateRequest,
} from '../types/myPage';
import { TimestampNumber } from '../../../../core/domain/types/dateType';

/**
 * 마이페이지 Hook
 */
export const useMyPage = () => {
  // 상태 관리
  const [myPageInfo, setMyPageInfo] = useState<MyPageInfoResponse | null>(null);
  const [accountForm, setAccountForm] = useState({
    bankCode: '',
    accountNum: '',
    accName: '',
  });
  const [depositAccountForm, setDepositAccountForm] = useState({
    dpBankCode: '',
    dpAcctNum: '',
    dpAcctName: '',
  });
  const [acctApplyType, setAcctApplyType] = useState<'W' | 'P'>('P'); // 'W': 주정산, 'P': 선정산 (기본값)
  const [acctApplyTypeName, setAcctApplyTypeName] = useState<string>('');
  const [preAcctApplyDate, setPreAcctApplyDate] = useState<string>('');
  const [nextAcctApplyType, setNextAcctApplyType] = useState<'W' | 'P' | null>(null);
  const [nextAcctApplyTypeName, setNextAcctApplyTypeName] = useState<string>('');
  const [nextAcctApplyDate, setNextAcctApplyDate] = useState<string>('');
  const [selectedNextSettlementType, setSelectedNextSettlementType] = useState<'W' | 'P'>('P'); // 하단 정산 섹션에서 사용자가 선택한 정산 타입
  const [hasUserChangedSettlement, setHasUserChangedSettlement] = useState<boolean>(false); // 사용자가 정산 타입을 변경했는지 추적
  // 즉시 접근 가능한 값들을 위한 ref
  const userSelectedTypeRef = useRef<'W' | 'P' | null>(null);
  const userSelectedDateRef = useRef<string>('');

  // API 호출 상태 관리
  const myPageInfoState = useAsync(getMyPageInfo);
  const settlementUpdateState = useAsync(updateMyPageSettlement);

  /**
   * 마이페이지 정보 조회
   */
  const fetchMyPageInfo = useCallback(async () => {
    // 관리자는 API 조회하지 않음
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo?.prmCode === 'A';
    
    if (isAdmin) {
      return;
    }
    
    try {
      // 토큰에 riderId, suppId가 포함되어 있으므로 파라미터에서 제외
      const responseData = await myPageInfoState.execute();
      setMyPageInfo(responseData);
      
      // 계정 정보 폼 초기화
      setAccountForm({
        bankCode: responseData.bankCode ?? '',
        accountNum: responseData.accountNum ?? '',
        accName: responseData.accName ?? '',
      });
      
      // 입금 계좌 정보 폼 초기화
      setDepositAccountForm({
        dpBankCode: responseData.dpBankCode ?? '',
        dpAcctNum: responseData.dpAcctNum ?? '',
        dpAcctName: responseData.dpAcctName ?? '',
      });
      
      // acctApplyType과 acctApplyDay 설정 (현재 적용 중)
      const currentType = responseData.acctApplyType === 'P' ? 'P' : (responseData.acctApplyType === 'W' ? 'W' : 'P');
      setAcctApplyType(currentType);
      setAcctApplyTypeName(responseData.acctApplyTypeName || '');
      
      // 적용일자 설정 (현재 적용 중)
      if (responseData.acctApplyDay) {
        setPreAcctApplyDate(formatApplyDate(responseData.acctApplyDay));
      }
      
      // 다음 정산 타입과 적용일자 설정 (변경 예정)
      if (responseData.nextAcctApplyType) {
        // 변경 예정이 있으면 변경 예정 타입으로 버튼 선택
        const nextType = responseData.nextAcctApplyType as 'W' | 'P';
        setNextAcctApplyType(nextType);
        setSelectedNextSettlementType(nextType);
        userSelectedTypeRef.current = nextType;
      } else {
        // 변경 예정이 없으면 현재 적용 중인 타입으로 버튼 선택
        setSelectedNextSettlementType(currentType);
        userSelectedTypeRef.current = currentType;
      }
      
      if (responseData.nextAcctApplyTypeName) {
        setNextAcctApplyTypeName(responseData.nextAcctApplyTypeName);
      }
      
      if (responseData.nextAcctApplyDay) {
        // 변경 예정일이 있으면 그 날짜 사용
        const formattedNextDate = formatApplyDate(responseData.nextAcctApplyDay);
        setNextAcctApplyDate(formattedNextDate);
        userSelectedDateRef.current = formattedNextDate;
      } else if (responseData.acctApplyDay) {
        // 변경 예정일이 없으면 현재 적용일 사용
        const formattedCurrentDate = formatApplyDate(responseData.acctApplyDay);
        setNextAcctApplyDate(formattedCurrentDate);
        userSelectedDateRef.current = formattedCurrentDate;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch mypage info:', err);
      }
    }
  }, []);


  /**
   * 정산 정보 업데이트 (계좌정보 + 입금계좌정보 + 정산타입)
   */
  const updateSettlement = useCallback(async () => {
    if (!accountForm.bankCode || !accountForm.accountNum || !accountForm.accName) {
      alert('모든 계좌 정보를 입력해주세요.');
      return;
    }

    // TODO: 입금계좌정보(depositAccountForm)는 직접 입력받을지, 조회(read-only)로 받을지 결정해야 함
    // if (!depositAccountForm.dpBankCode || !depositAccountForm.dpAcctNum || !depositAccountForm.dpAcctName) {
    //   alert('모든 입금 계좌 정보를 입력해주세요.');
    //   return;
    // }

    try {
      // 토큰에 riderId, suppId가 포함되어 있으므로 파라미터에서 제외
      const requestData: MyPageSettlementUpdateRequest = {
        bankCode: accountForm.bankCode,
        accountNum: accountForm.accountNum,
        accName: accountForm.accName,
        dpBankCode: depositAccountForm.dpBankCode,
        dpAcctNum: depositAccountForm.dpAcctNum,
        dpAcctName: depositAccountForm.dpAcctName,
        nextAcctApplyType: userSelectedTypeRef.current || '',
        nextAcctApplyDay: userSelectedDateRef.current ? unformatApplyDate(userSelectedDateRef.current) : '',
      };

      await settlementUpdateState.execute(requestData);
      alert('정산 정보가 성공적으로 업데이트되었습니다.');
      
      // 정보 다시 조회
      await fetchMyPageInfo();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to update settlement:', err);
      }
      alert('정산 정보 업데이트에 실패했습니다.');
    }
  }, [accountForm, depositAccountForm, fetchMyPageInfo]);

  /**
   * 계정 폼 필드 업데이트
   */
  const updateAccountForm = useCallback((field: keyof typeof accountForm, value: string) => {
    setAccountForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  /**
   * 입금 계좌 폼 필드 업데이트
   */
  const updateDepositAccountForm = useCallback((field: keyof typeof depositAccountForm, value: string) => {
    setDepositAccountForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // 차주 수요일 날짜 계산 함수
  const getNextWednesday = useCallback(() => {
    const today = new Date();
    const currentDay = today.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    
    // 오늘이 수요일(3)이면 오늘 날짜 반환, 아니면 다음 수요일 계산
    let daysToAdd = 0;
    if (currentDay === 3) {
      // 오늘이 수요일이면 오늘 날짜 사용
      daysToAdd = 0;
    } else {
      // 다음 수요일까지의 일수 계산
      daysToAdd = (3 - currentDay + 7) % 7;
      // 0이면 다음 주 수요일로 (일요일인 경우)
      if (daysToAdd === 0) {
        daysToAdd = 7;
      }
    }
    
    const nextWednesday = new Date(today);
    nextWednesday.setDate(today.getDate() + daysToAdd);
    
    const year = nextWednesday.getFullYear();
    const month = String(nextWednesday.getMonth() + 1).padStart(2, '0');
    const day = String(nextWednesday.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }, []);

  /**
   * 정산 타입 업데이트 (하단 정산 섹션용)
   */
  const updateAcctApplyType = useCallback((type: 'W' | 'P') => {
    setSelectedNextSettlementType(type);
    
    // nextAcctApplyType 설정
    setNextAcctApplyType(type);
    
    // nextAcctApplyDay 설정 (사용자가 변경할 때만 차주 수요일로 설정)
    const nextWednesday = getNextWednesday();
    setNextAcctApplyDate(nextWednesday);
    
    // 사용자가 변경했음을 표시하고 직접 값 저장
    setHasUserChangedSettlement(true);
    userSelectedTypeRef.current = type;
    userSelectedDateRef.current = nextWednesday;
  }, [getNextWednesday]);

  /**
   * 적용일자 업데이트 (하단 정산 섹션용)
   */
  const updateNextAcctApplyDate = useCallback((date: string) => {
    setNextAcctApplyDate(date);
    
    // 사용자가 변경했음을 표시하고 직접 값 저장
    setHasUserChangedSettlement(true);
    userSelectedDateRef.current = date;
  }, []);

  /**
   * 데이터 새로고침
   */
  const refreshData = useCallback(() => {
    // 에러 상태 초기화
    myPageInfoState.reset();
    settlementUpdateState.reset();
    
    // 데이터 다시 조회
    fetchMyPageInfo();
  }, [fetchMyPageInfo, myPageInfoState, settlementUpdateState]);

  // 컴포넌트 마운트 시 데이터 조회
  useEffect(() => {
    // 관리자는 API 조회하지 않음
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo?.prmCode === 'A';

    if (!isAdmin) {
      fetchMyPageInfo();
    }
  }, [fetchMyPageInfo]);

  // timestamp 배열을 날짜 포맷으로 변환하는 헬퍼
  const formatTimestampArray = useCallback((timestampArray: TimestampNumber) => {
    if (!timestampArray || timestampArray.length !== 6) return '';
    
    try {
      const [year, month, day, hours, minutes, seconds] = timestampArray;
      const date = new Date(year, month - 1, day, hours, minutes, seconds); // month는 0부터 시작
      
      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
      const weekday = weekdays[date.getDay()];
      
      // 12시간 형식으로 변환
      const ampm = hours >= 12 ? '오후' : '오전';
      let displayHours = hours;
      if (hours === 0) {
        displayHours = 12; // 자정 (0시)은 12시로 표시
      } else if (hours > 12) {
        displayHours = hours - 12; // 13시 이후는 1시부터 표시
      }
      
      return `${year}. ${String(month).padStart(2, '0')}. ${String(day).padStart(2, '0')} (${weekday}) ${ampm} ${displayHours}:${String(minutes).padStart(2, '0')}`;
    } catch {
      return '';
    }
  }, []);

  // 생년월일 포맷팅 헬퍼
  const formatBirthDate = useCallback((birthDate: string) => {
    if (!birthDate || birthDate.length !== 8) return birthDate;
    
    const year = birthDate.substring(0, 4);
    const month = birthDate.substring(4, 6);
    const day = birthDate.substring(6, 8);
    
    return `${year}-${month}-${day}`;
  }, []);

  // 주민등록번호 포맷팅 헬퍼
  const formatRegistNum = useCallback((registNum: string) => {
    if (!registNum || registNum.length !== 13) return registNum;
    
    const first = registNum.substring(0, 6);
    const second = registNum.substring(6, 13);
    
    return `${first}-${second}`;
  }, []);

  // 휴대폰 번호 포맷팅 헬퍼
  const formatPhoneNumber = useCallback((phoneNum: string) => {
    if (!phoneNum) return phoneNum;
    
    // 숫자만 추출
    const numbers = phoneNum.replace(/\D/g, '');
    
    if (numbers.length === 11) {
      // 11자리: 3-4-4 형식
      return `${numbers.substring(0, 3)}-${numbers.substring(3, 7)}-${numbers.substring(7, 11)}`;
    } else if (numbers.length === 10) {
      // 10자리: 3-3-4 형식
      return `${numbers.substring(0, 3)}-${numbers.substring(3, 6)}-${numbers.substring(6, 10)}`;
    }
    
    // 10자리나 11자리가 아니면 원본 반환
    return phoneNum;
  }, []);

  // 적용일자 포맷팅 헬퍼 (yyyymmdd -> yyyy-mm-dd)
  const formatApplyDate = useCallback((applyDate: string) => {
    if (!applyDate || applyDate.length !== 8) return applyDate;
    
    const year = applyDate.substring(0, 4);
    const month = applyDate.substring(4, 6);
    const day = applyDate.substring(6, 8);
    
    return `${year}-${month}-${day}`;
  }, []);

  // 적용일자 역포맷팅 헬퍼 (yyyy-mm-dd -> yyyymmdd)
  const unformatApplyDate = useCallback((applyDate: string) => {
    if (!applyDate) return applyDate;
    
    // 하이픈 제거
    return applyDate.replace(/-/g, '');
  }, []);

  return {
    // 상태
    myPageInfo,
    accountForm,
    depositAccountForm,
    acctApplyType,
    selectedNextSettlementType,
    
    // API 상태
    isLoading: myPageInfoState.isLoading,
    isUpdating: settlementUpdateState.isLoading,
    error: myPageInfoState.error || settlementUpdateState.error,
    
    // 액션
    updateAccountForm,
    updateDepositAccountForm,
    updateAcctApplyType,
    updateNextAcctApplyDate,
    updateSettlement,
    refreshData,
    
    // 적용일자 관련
    preAcctApplyDate,
    setPreAcctApplyDate,
    acctApplyTypeName,
    nextAcctApplyType,
    nextAcctApplyTypeName,
    nextAcctApplyDate,
    
    // 헬퍼
    formatTimestampArray,
    formatBirthDate,
    formatRegistNum,
    formatPhoneNumber,
    formatApplyDate,
    unformatApplyDate,
    getNextWednesday,
    isDataLoaded: !!myPageInfo,
  };
};
